import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  deleteCall,
  deleteCallRecording,
  fetchAllCallLogs,
  generatePresignedUrlsForCalls,
  saveCall,
  setCallerInfo,
  updateCalls,
  updateCallsById,
} from '@store/actions/calls.action'
import { addNote, updateNote, removeNote } from '@store/actions'
import { ICalls } from '@dataTypes/calls.type'
import { Device, Call } from '@twilio/voice-sdk'
import moment from 'moment'
import { INote, NoteContextType } from '@dataTypes/note.type'
import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'

type CallsState = {
  data: ICalls[]
  device: Device | null
  currentCall: Call | null
  callerInfo: Partial<ICalls> | null
  hasActiveCall: boolean
  durationIndex: number
  searchQuery: string
  filters: FilterSelection
  error: string | null
  loading: boolean
}

const initialState: CallsState = {
  data: [],
  device: null,
  currentCall: null,
  callerInfo: null,
  hasActiveCall: false,
  durationIndex: 0,
  searchQuery: '',
  filters: {
    type: [],
    status: [],
    sentiment: [],
  },
  loading: false,
  error: null,
}

const callsSlice = createSlice({
  name: 'calls',
  initialState,
  reducers: {
    setDurationIndex: (state, action: PayloadAction<number>) => {
      state.durationIndex = action.payload
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    setFilters: (state, action: PayloadAction<FilterSelection>) => {
      state.filters = action.payload
    },
    setDevice: (state, action: PayloadAction<Device | null>) => {
      state.device = action.payload
      state.error = null
    },
    setCurrentCall: (state, action: PayloadAction<Call | null>) => {
      state.currentCall = action.payload
      state.error = null
    },
    setHasActiveCall: (state, action: PayloadAction<boolean>) => {
      state.hasActiveCall = action.payload
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        setCallerInfo.fulfilled,
        (
          state,
          action: PayloadAction<{ callerInfo: Partial<ICalls> | null }>,
        ) => {
          state.callerInfo = action.payload.callerInfo
          state.error = null
        },
      )
      .addCase(setCallerInfo.pending, (state) => {
        state.error = null
      })
      .addCase(setCallerInfo.rejected, (state, action) => {
        state.error = action.payload ?? 'Failed to set caller information'
      })

    builder
      .addCase(
        fetchAllCallLogs.fulfilled,
        (state, action: PayloadAction<ICalls[]>) => {
          const payload = Array.isArray(action.payload) ? action.payload : []
          state.data = payload.sort((a, b) => {
            return moment(b.start_date_time).diff(moment(a.start_date_time))
          })
          state.loading = false
        },
      )
      .addCase(fetchAllCallLogs.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchAllCallLogs.rejected, (state) => {
        state.error = 'Failed to load messages'
        state.loading = false
      })

    builder
      .addCase(
        updateCalls.fulfilled,
        (state, action: PayloadAction<ICalls>) => {
          if (!state.data) return
          const index =
            state.data.findIndex(
              (call) => call.call_id === action.payload.call_id,
            ) || -1
          if (index !== -1) {
            const updatedData = {
              ...state.data[index],
              ...action.payload,
            }
            state.data[index] = updatedData
          }
          state.loading = false
        },
      )
      .addCase(updateCalls.pending, (state) => {
        state.loading = true
      })
      .addCase(updateCalls.rejected, (state) => {
        state.error = 'Failed to update call by number'
        state.loading = false
      })

    builder
      .addCase(
        updateCallsById.fulfilled,
        (state, action: PayloadAction<ICalls>) => {
          state.callerInfo = action.payload
          if (state.data) {
            const index = state.data.findIndex(
              (call) => call.call_id === action.payload.call_id,
            )
            if (index !== -1) {
              state.data[index] = action.payload
            }

            state.data = state.data.sort((a, b) => {
              return moment(b.start_date_time).diff(moment(a.start_date_time))
            })
          }
          state.loading = false
          state.error = null
        },
      )
      .addCase(updateCallsById.pending, (state) => {
        state.loading = true
      })
      .addCase(updateCallsById.rejected, (state) => {
        state.error = 'Failed to update call by id'
        state.loading = false
      })

    builder
      .addCase(
        saveCall.fulfilled,
        (state, action: PayloadAction<{ callerInfo: Partial<ICalls> }>) => {
          const { callerInfo } = action.payload
          if (callerInfo.call_id) {
            state.callerInfo = callerInfo
            state.data?.push(callerInfo as ICalls)
          }
          state.loading = false
          state.error = null
        },
      )
      .addCase(saveCall.pending, (state) => {
        state.loading = true
      })
      .addCase(saveCall.rejected, (state) => {
        state.error = 'Failed to set caller information'
        state.loading = false
      })

    builder
      .addCase(deleteCall.fulfilled, (state, action: PayloadAction<string>) => {
        if (!state.data) return
        state.data = state.data.filter(
          (call) => call.call_id !== action.payload,
        )
        state.loading = false
        state.error = null
      })
      .addCase(deleteCall.pending, (state) => {
        state.loading = true
      })
      .addCase(deleteCall.rejected, (state) => {
        state.error = 'Failed to delete call'
        state.loading = false
      })

    builder
      .addCase(
        deleteCallRecording.fulfilled,
        (state, action: PayloadAction<string>) => {
          if (!state.data) return
          const index = state.data.findIndex(
            (call) => call.call_id === action.payload,
          )
          if (index >= 0) {
            state.data[index] = {
              ...state.data[index],
              recording_url: null,
            }
          }
          state.loading = false
          state.error = null
        },
      )
      .addCase(deleteCallRecording.pending, (state) => {
        state.loading = true
      })
      .addCase(deleteCallRecording.rejected, (state) => {
        state.error = 'Failed to delete call recording'
        state.loading = false
      })

    builder
      .addCase(addNote.pending, (state) => {
        state.loading = true
      })
      .addCase(addNote.fulfilled, (state, action: PayloadAction<INote>) => {
        state.loading = false
        if (!state.data || action.payload.context_type !== NoteContextType.CALL)
          return
        const note = action.payload
        const callId = note.context_id ?? ''
        const index = state.data.findIndex((c) => c.call_id === callId)
        if (index !== -1) {
          state.data[index] = {
            ...state.data[index],
            call_notes: [...(state.data[index].call_notes ?? []), note],
          }
        }
        state.error = null
      })
      .addCase(addNote.rejected, (state, action) => {
        state.loading = false
        state.error =
          (action.payload as string) ??
          action.error?.message ??
          'Failed to add call note'
      })
      .addCase(updateNote.pending, (state) => {
        state.loading = true
      })
      .addCase(updateNote.fulfilled, (state, action: PayloadAction<INote>) => {
        state.loading = false
        if (!state.data || action.payload.context_type !== NoteContextType.CALL)
          return
        const updatedNote = action.payload
        state.data = state.data.map((call) => ({
          ...call,
          call_notes: call.call_notes?.map((note) =>
            note.note_id === updatedNote.note_id ? updatedNote : note,
          ),
        }))
        state.error = null
      })
      .addCase(updateNote.rejected, (state, action) => {
        state.loading = false
        state.error =
          (action.payload as string) ??
          action.error?.message ??
          'Failed to update call note'
      })
      .addCase(removeNote.pending, (state) => {
        state.loading = true
      })
      .addCase(removeNote.fulfilled, (state, action: PayloadAction<string>) => {
        if (!state.data) return
        state.data = state.data.map((call) => ({
          ...call,
          call_notes: call.call_notes?.filter(
            (note) => note.note_id !== action.payload,
          ),
        }))
        state.loading = false
        state.error = null
      })
      .addCase(removeNote.rejected, (state, action) => {
        state.loading = false
        state.error =
          (action.payload as string) ??
          action.error?.message ??
          'Failed to delete call note'
      })

    builder
      .addCase(
        generatePresignedUrlsForCalls.fulfilled,
        (state, action: PayloadAction<ICalls[]>) => {
          if (!state.data) return
          // Update only the calls that were passed in with their presigned URLs
          const updatedCallIds = new Set(
            action.payload.map((call) => call.call_id),
          )
          // Create a new array to ensure Syncfusion detects the change
          state.data = state.data.map((call) => {
            if (updatedCallIds.has(call.call_id)) {
              const updatedCall = action.payload.find(
                (c) => c.call_id === call.call_id,
              )
              // Return new object reference even if values are the same to force re-render
              return updatedCall ? { ...updatedCall } : call
            }
            return call
          })
          state.loading = false
          state.error = null
        },
      )
      .addCase(generatePresignedUrlsForCalls.pending, (state) => {
        state.loading = true
      })
      .addCase(generatePresignedUrlsForCalls.rejected, (state) => {
        state.error = 'Failed to generate presigned URLs'
        state.loading = false
      })
  },
})

export const {
  setDurationIndex,
  setSearchQuery,
  setFilters,
  setDevice,
  setCurrentCall,
  setHasActiveCall,
} = callsSlice.actions

export default callsSlice.reducer
